/**
 * PermissionConfigPage Component
 * 
 * Página dedicada para configuração de permissões de grupos de acesso.
 * Substitui o modal anterior por uma página completa seguindo o estilo do StockAdjustmentDashboard.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Shield, Save, Loader2, Bell } from 'lucide-react'
import PermissionManager from '../../../../components/ERP/PermissionManager/PermissionManager'
import { accessGroupService } from '../../../../services/erpApi'
import { usePermissions } from '../../../../hooks/usePermissions'
import { buildPermissionsForEditor, buildPermissionsForSave } from '../../../../utils/permissions'
import { useAuth } from '../../../../contexts/AuthContext'
import { useToast } from '../../../../hooks/useToast'
import { Toast } from '../../../../components'
import './PermissionConfigPage.css'

function PermissionConfigPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { user } = useAuth()
  const { refreshPermissions } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  
  const [grupo, setGrupo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [permissions, setPermissions] = useState({})
  const [permissionsRevision, setPermissionsRevision] = useState(0)
  const [notificationPermissions, setNotificationPermissions] = useState({
    purchases: {
      approval: false,
      sent_to_review: false,
      review_finalized: false,
      mirror_approved: false
    },
    orders: {
      created: false
    },
    profile: {
      complete_professional: false
    }
  })
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadGrupo = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const response = await accessGroupService.getById(id)
        
        if (response.success && response.data) {
          const grupoData = response.data
          setGrupo(grupoData)
          
          // Parse permissions se for string
          const parsedPermissions = typeof grupoData.permissions === 'string' 
            ? JSON.parse(grupoData.permissions) 
            : (grupoData.permissions || {})
          
          setPermissions(buildPermissionsForEditor(parsedPermissions))
          setPermissionsRevision((r) => r + 1)
          
          // Parse notification_permissions se for string
          const parsedNotificationPermissions = typeof grupoData.notification_permissions === 'string' 
            ? JSON.parse(grupoData.notification_permissions) 
            : (grupoData.notification_permissions || { purchases: { approval: false, sent_to_review: false } })
          
          // Garantir estrutura completa
          if (!parsedNotificationPermissions.purchases) {
            parsedNotificationPermissions.purchases = { approval: false, sent_to_review: false, review_finalized: false }
          }
          if (parsedNotificationPermissions.purchases.approval === undefined) {
            parsedNotificationPermissions.purchases.approval = false
          }
          if (parsedNotificationPermissions.purchases.sent_to_review === undefined) {
            parsedNotificationPermissions.purchases.sent_to_review = false
          }
          if (parsedNotificationPermissions.purchases.review_finalized === undefined) {
            parsedNotificationPermissions.purchases.review_finalized = false
          }
          if (parsedNotificationPermissions.purchases.mirror_approved === undefined) {
            parsedNotificationPermissions.purchases.mirror_approved = false
          }
          if (!parsedNotificationPermissions.orders) {
            parsedNotificationPermissions.orders = { created: false }
          }
          if (parsedNotificationPermissions.orders.created === undefined) {
            parsedNotificationPermissions.orders.created = false
          }
          if (!parsedNotificationPermissions.profile) {
            parsedNotificationPermissions.profile = { complete_professional: false }
          }
          if (parsedNotificationPermissions.profile.complete_professional === undefined) {
            parsedNotificationPermissions.profile.complete_professional = false
          }
          
          setNotificationPermissions(parsedNotificationPermissions)
        } else {
          setError('Grupo não encontrado')
          showToast('Grupo não encontrado', 'error')
        }
      } catch (error) {
        console.error('Erro ao carregar grupo:', error)
        const errorMessage = error.response?.data?.message || 'Erro ao carregar grupo'
        setError(errorMessage)
        showToast(errorMessage, 'error')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      loadGrupo()
    }
  }, [id, showToast])

  const handlePermissionsChange = (newPermissions) => {
    setPermissions(newPermissions)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      const dataToSend = {
        name: grupo.name,
        description: grupo.description || null,
        active: grupo.active !== undefined ? grupo.active : true,
        permissions: buildPermissionsForSave(permissions),
        notification_permissions: notificationPermissions
      }

      const response = await accessGroupService.update(id, dataToSend)

      if (response.success) {
        const reload = await accessGroupService.getById(id)
        if (reload.success && reload.data) {
          const reloaded = typeof reload.data.permissions === 'string'
            ? JSON.parse(reload.data.permissions)
            : (reload.data.permissions || {})
          setPermissions(buildPermissionsForEditor(reloaded))
          setPermissionsRevision((r) => r + 1)
        }

        if (user?.access_group_id === parseInt(id)) {
          refreshPermissions()
        }

        showToast('Permissões salvas com sucesso!', 'success')

        setTimeout(() => {
          navigate('/erp/pessoas/grupos')
        }, 1000)
      } else {
        const errorMessage = response.message || 'Erro ao salvar permissões'
        showToast(errorMessage, 'error')
      }
    } catch (error) {
      console.error('Erro ao salvar permissões:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar permissões'
      showToast(errorMessage, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    navigate('/erp/pessoas/grupos')
  }

  if (loading) {
    return (
      <div className="permission-config-page">
        <div className="permission-config-loading">
          <Loader2 size={32} className="permission-config-spinner" />
          <p>Carregando configurações...</p>
        </div>
      </div>
    )
  }

  if (error || !grupo) {
    return (
      <div className="permission-config-page">
        <div className="permission-config-error">
          <p>{error || 'Grupo não encontrado'}</p>
          <button 
            className="permission-config-btn-secondary"
            onClick={() => navigate('/erp/pessoas/grupos')}
          >
            <ArrowLeft size={16} />
            Voltar para Grupos
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="permission-config-page">
      {/* Header */}
      <div className="permission-config-header">
        <div>
          <h1 className="permission-config-title">
            Configuração de <span className="permission-config-title-accent">Permissões</span>
          </h1>
          <p className="permission-config-subtitle">
            Configure as permissões granulares para o grupo <strong>{grupo.name}</strong>
          </p>
        </div>
        <div className="permission-config-actions">
          <button 
            className="permission-config-btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            <ArrowLeft size={16} />
            Cancelar
          </button>
          <button 
            className="permission-config-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="permission-config-spinner" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar Permissões
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="permission-config-info-card">
        <div className="permission-config-info-header">
          <Shield size={20} />
          <h3>Informações do Grupo</h3>
        </div>
        <div className="permission-config-info-content">
          <div className="permission-config-info-item">
            <span className="permission-config-info-label">Nome do Grupo</span>
            <span className="permission-config-info-value">{grupo.name}</span>
          </div>
          {grupo.description && (
            <div className="permission-config-info-item">
              <span className="permission-config-info-label">Descrição</span>
              <span className="permission-config-info-value">{grupo.description}</span>
            </div>
          )}
          <div className="permission-config-info-item">
            <span className="permission-config-info-label">Status</span>
            <span className={`permission-config-status-badge ${grupo.active ? 'active' : 'inactive'}`}>
              {grupo.active ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        </div>
      </div>

      {/* Permissions Card */}
      <div className="permission-config-card">
        <div className="permission-config-card-header">
          <h3 className="permission-config-card-title">Permissões Granulares</h3>
          <p className="permission-config-card-description">
            Selecione os módulos e ações que este grupo pode realizar. 
            Use os controles para selecionar todas as permissões de um módulo ou todas as permissões de uma vez.
          </p>
        </div>
        <div className="permission-config-card-content">
          <PermissionManager
            key={`${id}-${permissionsRevision}`}
            permissions={permissions}
            onChange={handlePermissionsChange}
          />
        </div>
      </div>

      {/* Notifications Card */}
      <div className="permission-config-card">
        <div className="permission-config-card-header">
          <h3 className="permission-config-card-title">Notificações</h3>
          <p className="permission-config-card-description">
            Configure quais tipos de notificações este grupo deve receber. 
            Quando uma ação for realizada no sistema, os grupos habilitados serão notificados.
          </p>
        </div>
        <div className="permission-config-card-content">
          {/* Notificação de Envio para Revisão */}
          <div className="permission-notification-section">
            <div className="permission-notification-header">
              <Bell size={16} />
              <span className="permission-notification-title">Compras - Envio para Revisão</span>
            </div>
            <div className="permission-notification-switch-container">
              <div className="switch__container">
                <input
                  id="notification-purchases-sent-to-review"
                  className="switch switch--shadow"
                  type="checkbox"
                  checked={notificationPermissions.purchases?.sent_to_review || false}
                  onChange={(e) => {
                    setNotificationPermissions({
                      ...notificationPermissions,
                      purchases: {
                        ...notificationPermissions.purchases,
                        sent_to_review: e.target.checked
                      }
                    })
                  }}
                />
                <label htmlFor="notification-purchases-sent-to-review"></label>
              </div>
              <p className="permission-notification-switch-label">
                {notificationPermissions.purchases?.sent_to_review 
                  ? 'Grupo receberá notificações quando requisições forem enviadas para revisão técnica' 
                  : 'Grupo não receberá notificações de envio para revisão'}
              </p>
            </div>
          </div>

          {/* Notificação de Finalização de Revisão Técnica */}
          <div className="permission-notification-section">
            <div className="permission-notification-header">
              <Bell size={16} />
              <span className="permission-notification-title">Compras - Finalização de Revisão Técnica</span>
            </div>
            <div className="permission-notification-switch-container">
              <div className="switch__container">
                <input
                  id="notification-purchases-review-finalized"
                  className="switch switch--shadow"
                  type="checkbox"
                  checked={notificationPermissions.purchases?.review_finalized || false}
                  onChange={(e) => {
                    setNotificationPermissions({
                      ...notificationPermissions,
                      purchases: {
                        ...notificationPermissions.purchases,
                        review_finalized: e.target.checked
                      }
                    })
                  }}
                />
                <label htmlFor="notification-purchases-review-finalized"></label>
              </div>
              <p className="permission-notification-switch-label">
                {notificationPermissions.purchases?.review_finalized 
                  ? 'Grupo receberá notificações quando revisões técnicas forem finalizadas' 
                  : 'Grupo não receberá notificações de finalização de revisão técnica'}
              </p>
            </div>
          </div>

          {/* Notificação de Aprovação de Match Fiscal */}
          <div className="permission-notification-section">
            <div className="permission-notification-header">
              <Bell size={16} />
              <span className="permission-notification-title">Compras - Aprovação de Match Fiscal</span>
            </div>
            <div className="permission-notification-switch-container">
              <div className="switch__container">
                <input
                  id="notification-purchases-mirror-approved"
                  className="switch switch--shadow"
                  type="checkbox"
                  checked={notificationPermissions.purchases?.mirror_approved || false}
                  onChange={(e) => {
                    setNotificationPermissions({
                      ...notificationPermissions,
                      purchases: {
                        ...notificationPermissions.purchases,
                        mirror_approved: e.target.checked
                      }
                    })
                  }}
                />
                <label htmlFor="notification-purchases-mirror-approved"></label>
              </div>
              <p className="permission-notification-switch-label">
                {notificationPermissions.purchases?.mirror_approved 
                  ? 'Grupo receberá notificações quando match fiscal for aprovado e liberado para financeiro' 
                  : 'Grupo não receberá notificações de aprovação de match fiscal'}
              </p>
            </div>
          </div>

          {/* Notificação de Aprovação de Requisições */}
          <div className="permission-notification-section">
            <div className="permission-notification-header">
              <Bell size={16} />
              <span className="permission-notification-title">Compras - Aprovação de Requisições</span>
            </div>
            <div className="permission-notification-switch-container">
              <div className="switch__container">
                <input
                  id="notification-purchases-approval"
                  className="switch switch--shadow"
                  type="checkbox"
                  checked={notificationPermissions.purchases?.approval || false}
                  onChange={(e) => {
                    setNotificationPermissions({
                      ...notificationPermissions,
                      purchases: {
                        ...notificationPermissions.purchases,
                        approval: e.target.checked
                      }
                    })
                  }}
                />
                <label htmlFor="notification-purchases-approval"></label>
              </div>
              <p className="permission-notification-switch-label">
                {notificationPermissions.purchases?.approval 
                  ? 'Grupo receberá notificações quando requisições forem aprovadas financeiramente' 
                  : 'Grupo não receberá notificações de aprovação de requisições'}
              </p>
            </div>
          </div>

          {/* Pedido online criado */}
          <div className="permission-notification-section">
            <div className="permission-notification-header">
              <Bell size={16} />
              <span className="permission-notification-title">Pedidos Online - Novo Pedido</span>
            </div>
            <div className="permission-notification-switch-container">
              <div className="switch__container">
                <input
                  id="notification-orders-created"
                  className="switch switch--shadow"
                  type="checkbox"
                  checked={notificationPermissions.orders?.created || false}
                  onChange={(e) => {
                    setNotificationPermissions({
                      ...notificationPermissions,
                      orders: {
                        ...notificationPermissions.orders,
                        created: e.target.checked
                      }
                    })
                  }}
                />
                <label htmlFor="notification-orders-created"></label>
              </div>
              <p className="permission-notification-switch-label">
                {notificationPermissions.orders?.created
                  ? 'Grupo receberá notificações quando um cliente criar um pedido na loja online'
                  : 'Grupo não receberá notificações de novos pedidos online'}
              </p>
            </div>
          </div>

          <div className="permission-notification-section">
            <div className="permission-notification-header">
              <Bell size={16} />
              <span className="permission-notification-title">Perfil - Classe profissional e assinatura</span>
            </div>
            <div className="permission-notification-switch-container">
              <div className="switch__container">
                <input
                  id="notification-profile-complete-professional"
                  className="switch switch--shadow"
                  type="checkbox"
                  checked={notificationPermissions.profile?.complete_professional || false}
                  onChange={(e) => {
                    setNotificationPermissions({
                      ...notificationPermissions,
                      profile: {
                        ...notificationPermissions.profile,
                        complete_professional: e.target.checked
                      }
                    })
                  }}
                />
                <label htmlFor="notification-profile-complete-professional"></label>
              </div>
              <p className="permission-notification-switch-label">
                {notificationPermissions.profile?.complete_professional
                  ? 'Grupo recebe um aviso para preencher a classe profissional e desenhar a assinatura. O aviso abre o perfil e some quando os dois estiverem prontos.'
                  : 'Grupo não recebe o aviso de classe profissional e assinatura'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  )
}

export default PermissionConfigPage
